import { Map } from 'immutable';
import _ from 'lodash';
import moment from 'moment-timezone';

import reduceDatasetsByViewblockState from '../../../utils/reduceDatasetsByViewblockState';
import convertNetcdfjsonToLineByLineDatum from '../../../utils/convertNetcdfjsonToLineByLineDatum';
import datetime2binLabel from '../../../utils/datetime2binLabel';

const mergeTemplateAndData = (state) => {
    let { datasets } = state;
    const { viewblockState, time } = state;
    const settings = viewblockState.settings;
    const databilities = viewblockState.databilities;
    const { headings } = settings;
    const reportTime = time ? moment(time.iso8601).tz(time.timezone) : null;

    if (reportTime === null || !reportTime.isValid()) {
        throw new Error(`Report time is invalid: ${reportTime}`);
    }

    if (!headings) {
        console.warn('Datatable headings not found');
        return state;
    }

    datasets = reduceDatasetsByViewblockState({ datasets, viewblockState });

    let databilitiesInUse = new Set([]);
    headings.forEach((heading) => {
        heading.databilities.forEach((datability) => {
            databilitiesInUse.add(datability);
        });
    });

    // Make backward compatible
    const headingsWithDecimalPoint = headings.map((heading) => {
        return {
            ...heading,
            dp: heading.colours.map((_, index) => (heading && heading.dp && typeof heading.dp[index] === 'number' ? heading.dp[index] : settings.dp || 2)),
            hideUnits: heading.databilityUnits.map(
                (databilityUnit, index) => (databilityUnit === 'deg' || databilityUnit === 'rad') && heading.directionalVisualization[index] !== 'original'
            ),
        };
    });
    databilitiesInUse = _.filter(
        _.map(Array.from(databilitiesInUse), (datability) => _.find(databilities, (fullDatability) => fullDatability.id == datability))
    );
    const datasetsInUse = new Set(_.map([...databilitiesInUse], (datability) => datability.datasetId));

    datasets = datasets
        .filter((dataset) => {
            if (dataset.isError || !!dataset.error) {
                return false;
            } else if (!datasetsInUse.has(dataset.datasetId)) {
                // Ignore any included datasets that aren't referenced by the table
                return false;
            } else {
                return true;
            }
        })
        .map((dataset) => {
            const data = convertNetcdfjsonToLineByLineDatum({ netcdfjson: dataset.cfjson });
            return {
                ...dataset,
                data,
            };
        });

    const dayRange = settings.dayRange || { min: -2, max: 7 };
    const binning = settings.binning || false;
    const vertical = settings.orientation === 'vertical';
    const fontSize = settings.fontSize || 100;
    const dp = settings.dp || 2; // max decimal places for display
    const showGroupLabels = settings.showGroupLabels == null ? true : settings.showGroupLabels;
    const rotateVerticalLabels = settings.hasOwnProperty('rotateVerticalLabels') ? settings.rotateVerticalLabels : true;
    const bins = (binning && settings.bins != null && settings.bins) || 4;
    const allDatas = _.flatten(_.map(datasets, 'data'));
    const allTimes = _.map(allDatas, 'time');

    let startTime = reportTime.clone().add(dayRange.min, 'days').startOf('day');
    let endTime = reportTime
        .clone()
        .add(dayRange.max - 1, 'days')
        .endOf('day');
    let mandatoryTimes = [];

    if (binning) {
        let i;
        for (i = 0; i <= endTime.diff(startTime, 'hours'); i += 24 / bins) {
            mandatoryTimes.push(startTime.clone().add(i, 'hours'));
        }
    }
    const unionSortedTimes = _.union(mandatoryTimes, allTimes);
    const sortedTimes = _.sortBy(
        _.filter(unionSortedTimes, (a) => moment.isMoment(a)),
        (a) => a.unix()
    );
    const uniqueSortedTimes = _.sortedUniqBy(sortedTimes, (a) => a.unix());
    const uniqueSortedTimesFiltered = _.filter(uniqueSortedTimes, (a) => a >= startTime && a <= endTime);
    const uniqueSortedTimesTZ = _.map(uniqueSortedTimesFiltered, (t) => t.tz(reportTime.tz()));
    const arrayOfTimes = !binning
        ? uniqueSortedTimesTZ
        : _.sortedUniqBy(
              _.map(uniqueSortedTimesTZ, (value) => datetime2binLabel({ bins, dt: value })),
              (value) => value.unix()
          );

    const { title, threatLimits, limits, offsetLeft, useFixedOffset, displayTableHeading, headerPadding } = settings;

    let { model } = state || Map({});

    model = {
        settings,
        datasets,
        databilities: databilitiesInUse,
        time,
        vertical,
        arrayOfTimes,
        headings: headingsWithDecimalPoint,
        fontSize,
        dp,
        showGroupLabels,
        binning,
        bins,
        rotateVerticalLabels,
        title,
        threatLimits,
        limits,
        offsetLeft,
        useFixedOffset,
        displayTableHeading,
        headerPadding,
    };

    return { ...state, model };
};

export default mergeTemplateAndData;
